import { useEffect, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppError } from '@st/contract';
import { DEFAULT_LOCALE, type Locale } from '@st/i18n';
import { AuthProvider, useAuth, type AuthProviderProps } from '../auth/AuthProvider';
import { I18nProvider } from '../i18n/I18nProvider';
import { ToastProvider } from '../ui/Toast';
import { SocketFactoryProvider } from './socket';
import type { SocketFactory } from '../hooks/useConversationSocket';

/** 4xx greške nemaju smisla ponavljati — samo mreža i 5xx. */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          if (error instanceof AppError && error.status < 500) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

/** Jezik dolazi iz korisnikovog profila; prebacivanje u profilu ga nadjačava do odjave. */
function LocaleBridge({ children }: { children: ReactNode }) {
  const { user, setApiLocale } = useAuth();
  const [override, setOverride] = useState<Locale | null>(null);
  const locale: Locale = override ?? user?.locale ?? DEFAULT_LOCALE;

  useEffect(() => {
    setApiLocale(locale);
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale, setApiLocale]);

  return (
    <I18nProvider locale={locale} onLocaleChange={setOverride}>
      {children}
    </I18nProvider>
  );
}

export interface ProvidersProps extends Omit<AuthProviderProps, 'children'> {
  children: ReactNode;
  queryClient?: QueryClient;
  /** Testovi ubacuju vlastitu sobu; bez ovoga bi razgovor otvarao pravi WebSocket. */
  socketFactory?: SocketFactory;
}

export function Providers({ children, queryClient, socketFactory, ...auth }: ProvidersProps) {
  const [client] = useState(() => queryClient ?? makeQueryClient());
  return (
    <QueryClientProvider client={client}>
      <AuthProvider {...auth}>
        <LocaleBridge>
          <SocketFactoryProvider {...(socketFactory ? { factory: socketFactory } : {})}>
            <ToastProvider>{children}</ToastProvider>
          </SocketFactoryProvider>
        </LocaleBridge>
      </AuthProvider>
    </QueryClientProvider>
  );
}
